/**
 * Hent ansatte (#294 ONB-04..06; onboarding handoff screens 03 and 04): choose where the people are
 * today, see them before anything is added, add the chosen ones, see each person's result.
 *
 * - Excel or CSV: the file is read and checked on the server (POST /admin/people-import/preview), which
 *   writes nothing. Avbryt before «Legg til» adds nobody.
 * - The chosen people are made one by one through POST /admin/users, so every rule of Ny ansatt holds
 *   and one refusal does not stop the rest. «Prøv igjen» reads the file again first: who was made is
 *   then «Finnes fra før», so a retry cannot make anyone twice.
 * - Microsoft 365 (ONB-07): the manager signs in at Microsoft, where an administrator of the company's
 *   Microsoft 365 must have consented to «Read all users' full profiles» (User.Read.All). The server
 *   reads the enabled member users once (services/msDirectory.js) and the same preview shows them with
 *   the directory, the account and the scope. Birth date is not in the directory: it is filled in per
 *   person here before that person can be chosen. No consent or no scope is said as it is, with Excel
 *   and Ny ansatt beside it.
 * - The payroll system's own import (PowerOffice Go, Tripletex) stays where it is, under Innstillinger →
 *   Lønn; manual entry is Ny ansatt. No provider logos: the Visma terms forbid Visma's
 *   (docs/integrations/poweroffice.md).
 *
 * Adding people adds people: no hours, no invitations, no approvals (the note under the result).
 */
import { api, del, errorText, get, post } from '@/api/client';
import { T } from '@/i18n';
import { useTenant } from '@/shell/tenant';
import { Button } from '@/ui';
import { DateInput } from '@/ui/DateInput';
import { dayMonth } from '@/ui/format';
import { Icon, type IconName } from '@/ui/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import s from './importer.module.css';

export interface FilePerson {
  row: number;
  name: string;
  lastname: string;
  email: string | null;
  phone: string | null;
  employee_id: string | null;
  birth_date: string | null;
  employment_start: string | null;
  agreed_week_hours: number | null;
  status: 'new' | 'exists' | 'refused';
  problems: string[];
  /** fields the source does not hold and the manager fills in here before choosing the person */
  missing?: 'birth_date'[];
  existing: { user_id: number; name: string } | null;
}
export interface FileRead {
  source: 'csv' | 'xlsx' | 'microsoft';
  /** a directory read: which directory, who signed in at Microsoft, the scope granted, when */
  provider?: {
    name: string;
    directory: string | null;
    directory_id: string;
    signed_in_as: string | null;
    scope: string;
    read_at: string;
  };
  columns: Record<string, string>;
  people: FilePerson[];
  counts: { new: number; exists: number; refused: number };
}
/** made; already there; refused by the file itself (a retry cannot help); failed when saved (a retry may) */
type Outcome = { person: FilePerson; result: 'made' | 'exists' | 'refused' | 'failed'; reason?: string };

export function readPeopleFile(file: File) {
  const fd = new FormData();
  fd.append('file', file);
  return api<FileRead>('/admin/people-import/preview', { method: 'POST', body: fd });
}
const MS = '/admin/people-import/microsoft';
export const readMicrosoft = () => get<FileRead>(MS);

/** Why a Microsoft read did not happen, in the words the callback's ?microsoft= code stands for. */
export const MS_ERRORS: Record<string, string> = {
  consent:
    'Microsoft 365 har ikke gitt Timefly lov til å lese brukerkatalogen. En administrator i Microsoft 365 må godkjenne tilgangen «Lese alle brukeres fullstendige profiler» (User.Read.All). Til da kan du bruke Excel eller CSV, eller legge inn de ansatte selv.',
  scope:
    'Microsoft ga tilgang, men ikke til å lese brukerne (User.Read.All). Ingen ansatte er hentet. En administrator i Microsoft 365 må godkjenne den tilgangen, eller du kan bruke Excel eller CSV.',
  personal:
    'Dette er en personlig Microsoft-konto. Bare en jobbkonto i Microsoft 365 har en brukerkatalog. Logg inn med jobbkontoen, eller bruk Excel eller CSV.',
  cancelled: 'Innloggingen hos Microsoft ble avbrutt. Ingen ansatte er hentet.',
  too_many:
    'Brukerkatalogen har mer enn 1000 aktive brukere, for mange å hente på én gang. Bruk Excel eller CSV, eller legg inn de ansatte selv.',
  state:
    'Innloggingen hos Microsoft ble ikke fullført i denne nettleseren. Ingen ansatte er hentet. Prøv igjen.',
  exchange: 'Microsoft svarte ikke som ventet. Ingen ansatte er hentet. Prøv igjen.',
  graph: 'Kunne ikke lese brukerkatalogen fra Microsoft. Ingen ansatte er hentet. Prøv igjen.',
};

/** Microsoft's answers that mean Timefly was not given access (no consent, no User.Read.All), not a failure (#154 HUG-03). */
export const MS_DENIED = ['consent', 'scope'];

/**
 * To Microsoft: the server's ticket posted as a form from this page, so the browser itself goes on. From
 * the setup (`back` 'oppsett') a refusal comes back to the setup; a read always opens Hent ansatte.
 */
export async function startMicrosoft(back?: 'oppsett') {
  const { go, ticket } = await post<{ go: string; ticket: string }>(`${MS}/start`, back ? { back } : {});
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = go;
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = 'ticket';
  input.value = ticket;
  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
}

// the username Ny ansatt would be typed with: the address, else name, surname and the file's row
const userOf = (p: FilePerson) =>
  (p.email ?? `${p.name}.${p.lastname}.${p.row}`).toLowerCase().replace(/\s+/g, '');
const keyOf = (p: FilePerson) => `${p.row}`;

/** Makes the chosen people one by one; a refusal is that person's result, not the end of the run. */
export async function addPeople(people: FilePerson[]): Promise<Outcome[]> {
  const out: Outcome[] = [];
  for (const p of people) {
    try {
      await post('/admin/users', {
        username: userOf(p),
        name: p.name,
        lastname: p.lastname,
        email: p.email ?? '',
        phone: p.phone ?? '',
        birth_date: p.birth_date,
        ...(p.employee_id ? { employee_id: p.employee_id } : {}),
        ...(p.employment_start ? { employment_start: p.employment_start } : {}),
        ...(p.agreed_week_hours != null ? { agreed_week_hours: p.agreed_week_hours } : {}),
        // nobody gets mail here: inviting is its own step (ONB-11)
        send_welcome: false,
      });
      out.push({ person: p, result: 'made' });
    } catch (e) {
      out.push({ person: p, result: 'failed', reason: errorText(e) });
    }
  }
  return out;
}

const SYSTEM_NAME = { poweroffice: 'PowerOffice Go', tripletex: 'Tripletex' } as const;

export default function Importer() {
  const { tenant } = useTenant();
  const qc = useQueryClient();
  const sys =
    tenant?.payroll_system === 'poweroffice' || tenant?.payroll_system === 'tripletex'
      ? tenant.payroll_system
      : null;
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [read, setRead] = useState<FileRead | null>(null);
  const [pick, setPick] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<'read' | 'add' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Outcome[] | null>(null);
  // birth dates typed on the preview, by row, for people whose source has none
  const [births, setBirths] = useState<Record<string, string>>({});
  const [sp, setSp] = useSearchParams();
  const ms = sp.get('microsoft');
  // back from Microsoft: show the read, or say why there is none, then forget the address's code
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per answer from Microsoft
  useEffect(() => {
    if (!ms) return;
    const n = new URLSearchParams(sp);
    n.delete('microsoft');
    setSp(n, { replace: true });
    if (ms !== 'ok') {
      setError(T(MS_ERRORS[ms] ?? (MS_ERRORS.graph as string)));
      return;
    }
    setBusy('read');
    readMicrosoft()
      .then((r) => {
        setRead(r);
        setPick(new Set(r.people.filter((p) => p.status === 'new' && !p.missing?.length).map(keyOf)));
      })
      .catch((e) => setError(errorText(e)))
      .finally(() => setBusy(null));
  }, [ms]);
  const microsoft = read?.source === 'microsoft';
  const withBirth = (p: FilePerson): FilePerson => {
    const b = births[keyOf(p)];
    return b ? { ...p, birth_date: b } : p;
  };
  const ready = (p: FilePerson) =>
    p.status === 'new' && (!p.missing?.includes('birth_date') || !!births[keyOf(p)]);

  const load = async (f: File) => {
    setBusy('read');
    setError(null);
    try {
      const r = await readPeopleFile(f);
      setFile(f);
      setRead(r);
      setPick(new Set(r.people.filter((p) => p.status === 'new').map(keyOf)));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  const cancel = () => {
    // the people read from Microsoft are not kept once the manager is done with them
    if (microsoft) del(MS).catch(() => undefined);
    setBirths({});
    setFile(null);
    setRead(null);
    setDone(null);
    setError(null);
    if (input.current) input.current.value = '';
  };
  const add = async (people: FilePerson[]) => {
    setBusy('add');
    const out = await addPeople(people.map(withBirth));
    // the rest of the file as it stood: who was already there, who could not be added
    const others: Outcome[] = (read?.people ?? [])
      .filter((p) => !people.some((x) => keyOf(x) === keyOf(p)))
      .filter((p) => p.status !== 'new')
      .map((p) => ({
        person: p,
        result: p.status === 'exists' ? 'exists' : 'refused',
        reason: p.problems.join(' '),
      }));
    setDone([...out, ...others]);
    // the import is done once nobody is left to retry: the people read from Microsoft go then, not an hour
    // later; while someone failed the read stays for «Prøv igjen», and the hour is only for an abandoned one
    if (microsoft && !out.some((o) => o.result === 'failed')) del(MS).catch(() => undefined);
    setBusy(null);
    qc.invalidateQueries({ queryKey: ['users'] });
    qc.invalidateQueries({ queryKey: ['onboarding'] });
  };
  // a retry reads the source again first, so whoever was made in the meantime is not made twice
  const retry = async () => {
    if ((!file && !microsoft) || !done) return;
    const failed = new Set(done.filter((o) => o.result === 'failed').map((o) => keyOf(o.person)));
    setBusy('add');
    try {
      const r = microsoft ? await readMicrosoft() : await readPeopleFile(file as File);
      setRead(r);
      await add(r.people.filter((p) => ready(p) && failed.has(keyOf(p))));
    } catch (e) {
      setError(errorText(e));
      setBusy(null);
    }
  };

  return (
    <div className={s.page} data-testid="importer">
      <Link className={s.back} to="/kom-i-gang">
        <Icon name="chevronLeft" size={16} />
        {T('Tilbake til Kom i gang')}
      </Link>
      <header className={s.head}>
        <span className={s.mark} aria-hidden="true">
          <Icon name="users" size={22} />
        </span>
        <div>
          <h1>{T('Hent ansatte')}</h1>
          <p>{T('Bruk opplysningene dere allerede har. Dere ser dem før noe legges til.')}</p>
        </div>
      </header>

      {done ? (
        <Result
          done={done}
          busy={busy === 'add'}
          company={tenant?.name ?? ''}
          onRetry={retry}
          onAgain={cancel}
          microsoft={microsoft}
        />
      ) : read ? (
        <Preview
          read={read}
          pick={pick}
          setPick={setPick}
          busy={busy === 'add'}
          onAdd={() => add(read.people.filter((p) => ready(p) && pick.has(keyOf(p))))}
          onCancel={cancel}
          births={births}
          setBirth={(k, v) => {
            setBirths((b) => ({ ...b, [k]: v }));
            const n = new Set(pick);
            if (v) n.add(k);
            else n.delete(k);
            setPick(n);
          }}
          ready={ready}
        />
      ) : (
        <section aria-labelledby="imp-src">
          <h2 id="imp-src" className={s.h2}>
            {T('Hvor har dere de ansatte i dag?')}
          </h2>
          <ul className={s.sources}>
            <li>
              <Source
                icon="upload"
                title={T('Excel eller CSV')}
                line={T('En liste med navn, fødselsdato og gjerne e-post, ansattnummer og arbeidstid.')}
              >
                <input
                  ref={input}
                  type="file"
                  accept=".xlsx,.csv,.txt"
                  className="sr-only"
                  id="imp-file"
                  data-testid="imp-file"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) load(f);
                  }}
                />
                <label htmlFor="imp-file" className="btn pri" aria-disabled={busy === 'read'}>
                  {busy === 'read' ? T('Leser filen …') : T('Velg fil')}
                </label>
              </Source>
            </li>
            <li>
              <Source
                icon="briefcase"
                title={T('Microsoft 365')}
                line={T(
                  'Navn, jobb-e-post, mobil og ansattnummer fra brukerkatalogen. Fødselsdato og arbeidstid fyller du inn i Timefly.',
                )}
              >
                <Button
                  disabled={busy !== null}
                  data-testid="imp-microsoft"
                  onClick={async () => {
                    setError(null);
                    try {
                      await startMicrosoft();
                    } catch (e) {
                      setError(errorText(e));
                    }
                  }}
                >
                  {T('Hent fra Microsoft 365')}
                </Button>
              </Source>
            </li>
            {sys && (
              <li>
                <Source
                  icon="banknote"
                  title={T('Fra {system}', { system: SYSTEM_NAME[sys] })}
                  line={T('Hent ansatte fra lønnssystemet dere bruker, under Innstillinger → Lønn.')}
                >
                  <Link className="btn" to={`/innstillinger/lonn#${sys}`}>
                    {T('Gå til {system}', { system: SYSTEM_NAME[sys] })}
                  </Link>
                </Source>
              </li>
            )}
            <li>
              <Source
                icon="userPlus"
                title={T('Legg inn selv')}
                line={T('Én og én, med de opplysningene du har.')}
              >
                <Link className="btn" to="/ansatte/ny">
                  {T('Ny ansatt')}
                </Link>
              </Source>
            </li>
          </ul>
          {error && (
            <p className={s.error} role="alert" data-testid="imp-error">
              {error}
            </p>
          )}
          <p className={s.hint}>
            {T(
              'Første rad i filen må ha overskrifter, for eksempel Fornavn, Etternavn, E-post, Ansattnr, Fødselsdato, Ansatt fra og Avtalt arbeidstid.',
            )}
          </p>
        </section>
      )}
    </div>
  );
}

function Source({
  icon,
  title,
  line,
  children,
}: { icon: IconName; title: string; line: string; children: React.ReactNode }) {
  return (
    <div className={s.source}>
      <span className={s.sIcon} aria-hidden="true">
        <Icon name={icon} size={20} />
      </span>
      <span className={s.sText}>
        <b>{title}</b>
        <span>{line}</span>
      </span>
      <span className={s.sAct}>{children}</span>
    </div>
  );
}

const STATUS = {
  new: 'Ny',
  exists: 'Finnes fra før',
  refused: 'Kan ikke legges til',
} as const;

function Preview({
  read,
  pick,
  setPick,
  busy,
  onAdd,
  onCancel,
  births,
  setBirth,
  ready,
}: {
  read: FileRead;
  pick: Set<string>;
  setPick: (s: Set<string>) => void;
  busy: boolean;
  onAdd: () => void;
  onCancel: () => void;
  births: Record<string, string>;
  setBirth: (key: string, value: string) => void;
  ready: (p: FilePerson) => boolean;
}) {
  const chosen = read.people.filter((p) => ready(p) && pick.has(keyOf(p))).length;
  const needBirth = read.people.filter((p) => p.status === 'new' && p.missing?.includes('birth_date')).length;
  const pv = read.provider;
  const toggle = (k: string) => {
    const n = new Set(pick);
    if (n.has(k)) n.delete(k);
    else n.add(k);
    setPick(n);
  };
  return (
    <section aria-labelledby="imp-prev" data-testid="imp-preview">
      <h2 id="imp-prev" className={s.h2}>
        {T('Se over før noe legges til')}
      </h2>
      <p className={s.sum}>
        {T('{n} nye, {e} finnes fra før, {r} kan ikke legges til.', {
          n: read.counts.new,
          e: read.counts.exists,
          r: read.counts.refused,
        })}{' '}
        {pv
          ? needBirth > 0 &&
            T('{n} mangler fødselsdato. Fyll den inn for dem du vil legge til.', { n: needBirth })
          : T('Kolonner lest: {c}.', { c: Object.values(read.columns).join(', ') })}
      </p>
      {pv && (
        <dl className={s.source2} data-testid="imp-provider">
          <div>
            <dt>{T('Kilde')}</dt>
            <dd>{pv.name}</dd>
          </div>
          <div>
            <dt>{T('Katalog')}</dt>
            <dd>{pv.directory ?? pv.directory_id}</dd>
          </div>
          <div>
            <dt>{T('Logget inn som')}</dt>
            <dd>{pv.signed_in_as ?? T('Ukjent')}</dd>
          </div>
          <div>
            <dt>{T('Tilgang')}</dt>
            <dd>{T('Lese brukere ({scope}), bare lesing', { scope: pv.scope })}</dd>
          </div>
          <div>
            <dt>{T('Hentet')}</dt>
            <dd>
              {dayMonth(pv.read_at.slice(0, 10))}{' '}
              {new Date(pv.read_at).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
            </dd>
          </div>
        </dl>
      )}
      <ul className={s.people}>
        {read.people.map((p) => (
          <li key={keyOf(p)} data-testid={`imp-row-${p.row}`} data-status={p.status}>
            {p.status === 'new' ? (
              <input
                type="checkbox"
                aria-label={T('Legg til {name}', { name: `${p.name} ${p.lastname}` })}
                checked={ready(p) && pick.has(keyOf(p))}
                disabled={!ready(p)}
                onChange={() => toggle(keyOf(p))}
              />
            ) : (
              <span className={s.noBox} aria-hidden="true" />
            )}
            <span className={s.pText}>
              <b>
                {p.name} {p.lastname}
              </b>
              <span>
                {[
                  p.email,
                  p.employee_id && T('Ansattnr {n}', { n: p.employee_id }),
                  p.agreed_week_hours != null
                    ? T('{h} t/uke', { h: String(p.agreed_week_hours).replace('.', ',') })
                    : T('Avtalt arbeidstid mangler'),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
              {p.status === 'exists' && p.existing && (
                <span>{T('Samme person som {name}.', { name: p.existing.name })}</span>
              )}
              {p.problems.length > 0 && <span className={s.why}>{p.problems.join(' ')}</span>}
              {p.status === 'new' && p.missing?.includes('birth_date') && (
                <span className={s.birth}>
                  <label htmlFor={`imp-birth-${p.row}`}>{T('Fødselsdato')}</label>
                  <DateInput
                    value={births[keyOf(p)] ?? ''}
                    onValueChange={(v) => setBirth(keyOf(p), v)}
                    id={`imp-birth-${p.row}`}
                    openMonth="1990-01"
                    data-testid={`imp-birth-${p.row}`}
                  />
                </span>
              )}
            </span>
            <span className={`${s.tag} ${s[p.status]}`}>{T(STATUS[p.status])}</span>
          </li>
        ))}
      </ul>
      <div className={s.foot}>
        <Button tone="pri" disabled={!chosen || busy} onClick={onAdd} data-testid="imp-add">
          {busy ? T('Legger til …') : T('Legg til {n} ansatte', { n: chosen })}
        </Button>
        <Button ghost onClick={onCancel} disabled={busy} data-testid="imp-cancel">
          {T('Avbryt')}
        </Button>
        <span className={s.hint}>{T('Ingen får e-post nå. Invitasjonen sender du når du vil.')}</span>
      </div>
    </section>
  );
}

const RESULT = {
  made: 'Lagret',
  exists: 'Fantes fra før',
  refused: 'Ikke lagret',
  failed: 'Ikke lagret',
} as const;

function Result({
  done,
  busy,
  company,
  onRetry,
  onAgain,
  microsoft = false,
}: {
  done: Outcome[];
  busy: boolean;
  company: string;
  onRetry: () => void;
  onAgain: () => void;
  microsoft?: boolean;
}) {
  const made = done.filter((o) => o.result === 'made').length;
  const failed = done.filter((o) => o.result === 'failed').length;
  return (
    <section aria-labelledby="imp-res" data-testid="imp-result">
      <h2 id="imp-res" className={s.h2} aria-live="polite">
        {made === 1 ? T('1 ansatt er lagt til') : T('{n} ansatte er lagt til', { n: made })}
      </h2>
      <ul className={s.people}>
        {done.map((o) => (
          <li key={keyOf(o.person)} data-testid={`imp-out-${o.person.row}`} data-result={o.result}>
            <span className={`${s.rIcon} ${s[o.result]}`} aria-hidden="true">
              <Icon name={o.result === 'failed' || o.result === 'refused' ? 'warn' : 'check'} size={16} />
            </span>
            <span className={s.pText}>
              <b>
                {o.person.name} {o.person.lastname}
              </b>
              {o.result === 'made' && <span>{T('Lagt til i {company}.', { company })}</span>}
              {o.reason && <span className={s.why}>{o.reason}</span>}
            </span>
            <span className={`${s.tag} ${s[o.result]}`}>{T(RESULT[o.result])}</span>
          </li>
        ))}
      </ul>
      {failed > 0 && (
        <div className={s.foot}>
          <Button onClick={onRetry} disabled={busy} data-testid="imp-retry">
            {busy ? T('Prøver igjen …') : T('Prøv igjen for {n} som ikke ble lagret', { n: failed })}
          </Button>
        </div>
      )}
      <div className={s.next}>
        <h3>{T('Ansatte er grunnlaget. Kontroll følger arbeidet.')}</h3>
        <p>
          {T(
            'Importen legger til personer. Den henter ingen timer, inviterer ingen og godkjenner ingenting.',
          )}
        </p>
        <ol className={s.steps}>
          <li>
            <Icon name="users" size={20} aria-hidden="true" />
            <span>
              <b>{T('Fullfør opplysningene')}</b>
              <span>{T('Avklar arbeidstid, regler og hvem som skal registrere.')}</span>
            </span>
          </li>
          <li>
            <Icon name="clock" size={20} aria-hidden="true" />
            <span>
              <b>{T('Registrer faktisk arbeid')}</b>
              <span>{T('Ansatte stempler i Timefly, eller leder registrerer start, slutt og pause.')}</span>
            </span>
          </li>
          <li>
            <Icon name="check" size={20} aria-hidden="true" />
            <span>
              <b>{T('Avklar og godkjenn i Kontroll')}</b>
              <span>
                {T('Rett mangler og se beregningen. Godkjente timer kan deretter bli lønnsgrunnlag.')}
              </span>
            </span>
          </li>
        </ol>
        <div className={s.foot}>
          <Link className="btn pri" to="/ansatte?oppgave=arbeidstid" data-testid="imp-finish">
            {T('Fullfør ansattopplysninger')}
          </Link>
          <Link className={s.link} to="/kom-i-gang">
            {T('Til Kom i gang')}
          </Link>
          <button type="button" className={s.link} onClick={onAgain}>
            {microsoft ? T('Hent fra en annen kilde') : T('Hent fra en annen fil')}
          </button>
        </div>
      </div>
    </section>
  );
}
